import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api/client';

interface SearchUser {
  id: string;
  fullName: string;
  appTerritory: string;
  isActive: boolean;
}

/**
 * Admin-only quick jump: type a name, pick a consultant, land on their score.
 * Loads the full user list once and filters client-side, so it's instant.
 */
export function UserSearch() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<SearchUser[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api.getSettingsUsers()
      .then(res => { if (!cancelled) setUsers(res.users || []); })
      .catch(() => { /* leave empty — the box just won't match anything */ });
    return () => { cancelled = true; };
  }, []);

  // Close the results when clicking outside.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter(u => u.fullName.toLowerCase().includes(q))
      // Active first, then alphabetical.
      .sort((a, b) => (Number(b.isActive) - Number(a.isActive)) || a.fullName.localeCompare(b.fullName))
      .slice(0, 8);
  }, [query, users]);

  const go = (u: SearchUser) => {
    setQuery('');
    setOpen(false);
    navigate(`/?userId=${u.id}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(matches[highlight] || matches[0]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="card" style={{ marginBottom: '24px' }}>
      <div className="card-header">Find a consultant</div>
      <div ref={boxRef} style={{ position: 'relative', marginTop: '8px' }}>
        <input
          type="text"
          value={query}
          placeholder="Search by name…"
          onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          style={{
            width: '100%',
            background: 'var(--bg-page)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-card)',
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '14px',
          }}
        />

        {open && query.trim() && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              background: 'var(--bg-card)',
              border: '1px solid var(--border-card)',
              borderRadius: '8px',
              overflow: 'hidden',
              zIndex: 20,
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            }}
          >
            {matches.length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                No one matches "{query.trim()}"
              </div>
            ) : (
              matches.map((u, i) => (
                <div
                  key={u.id}
                  onMouseDown={() => go(u)}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    padding: '9px 12px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    background: i === highlight ? 'rgba(93,143,154,0.12)' : 'transparent',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    {u.fullName}
                    {!u.isActive && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-secondary)' }}>(inactive)</span>
                    )}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{u.appTerritory}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
